import {
    EmptyMessage,
    HistoryItem,
    HistoryItemDate,
    HistoryItemHours,
    HistoryItemJob,
    HistoryItemLeft,
    HistoryItemsContainer,
    HistorySection,
    HistoryTitleWrapper,
    HistoryToggleIcon,
    TitleWithIcon,
} from "../custom-styles/HistorySection.styles";
import React, { useMemo, useState } from "react";
import Text, { VARIANTS } from "shared/components/data-display/text/Text";
import {
    differenceInCalendarDays,
    format,
    isThisWeek,
    isToday,
    isYesterday,
} from "date-fns";

import COLORS from "shared/config/colors";
import CircularProgress from "@material-ui/core/CircularProgress";
import HistoryIcon from "@material-ui/icons/History";
import PropTypes from "prop-types";
import Tooltip from "shared/components/utils/tooltip/Tooltip";

function HistorySectionComponent({
    recentEntriesData,
    assignmentsData,
    departmentAssignmentsData,
    onJobSelect,
}) {
    // Internal state for expansion
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

    // Process entries from API
    const processedEntries = useMemo(() => {
        if (!recentEntriesData?.data) {
            return [];
        }

        // Create lookup map: projectId -> { name, client } (from both sources)
        const projectMap = {};

        assignmentsData?.data?.forEach((a) => {
            projectMap[a.ProjectId] = {
                name: a.ProjectName,
                client: a.ClientName,
            };
        });

        departmentAssignmentsData?.data?.forEach((a) => {
            projectMap[a.ProjectId] = {
                name: a.ProjectName,
                client: "Non-Client-Related Activities",
            };
        });

        // Filter and map entries
        return recentEntriesData.data
            .filter((entry) => {
                const projectId = entry.projectId || entry.jobId;
                return projectId && projectMap[projectId];
            })
            .map((entry, index) => {
                // Convert TimeSpan from seconds to hours
                const hours = (entry.timeSpan / 3600).toFixed(2);

                const projectId = entry.projectId || entry.jobId;
                const { name: projectName, client: clientName } =
                    projectMap[projectId];

                // Format date with relative label + date
                const entryDate = new Date(entry.enteredOn);
                let relativeLabel;

                if (isToday(entryDate)) {
                    relativeLabel = "Today";
                } else if (isYesterday(entryDate)) {
                    relativeLabel = "Yesterday";
                } else if (
                    differenceInCalendarDays(new Date(), entryDate) === 2
                ) {
                    relativeLabel = "2 days ago";
                } else if (isThisWeek(entryDate, { weekStartsOn: 1 })) {
                    relativeLabel = format(entryDate, "EEEE");
                } else {
                    relativeLabel = "Last week";
                }

                // Format date only (without time)
                const formattedDate = format(entryDate, "MMM d");
                const dateLabel = `Saved: ${relativeLabel} - ${formattedDate}`;

                const timeDayLabel = entry.timeDay
                    ? format(new Date(entry.timeDay), "EEE d")
                    : null;

                return {
                    id:
                        entry.id ||
                        `${projectId}-${entry.enteredOn}-${entry.timeSpan}-${index}`,
                    job: projectName,
                    clientName,
                    projectId,
                    hours: Number.parseFloat(hours),
                    date: dateLabel,
                    rawDate: entry.enteredOn,
                    timeDayLabel,
                };
            });
    }, [
        recentEntriesData?.data,
        assignmentsData?.data,
        departmentAssignmentsData?.data,
    ]);

    const isLoading = recentEntriesData?.isFetching || false;

    return (
        <HistorySection>
            <HistoryTitleWrapper
                onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
            >
                <TitleWithIcon>
                    <HistoryIcon />
                    <Text variant={VARIANTS.Headline_4} color={COLORS.Black}>
                        Recent Entries
                    </Text>
                </TitleWithIcon>
                <HistoryToggleIcon expanded={isHistoryExpanded ? 1 : 0} />
            </HistoryTitleWrapper>
            <HistoryItemsContainer expanded={isHistoryExpanded ? 1 : 0}>
                {(() => {
                    if (isLoading) {
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    padding: "16px",
                                }}
                            >
                                <CircularProgress
                                    size={20}
                                    thickness={4}
                                    style={{ color: "#9ca3af" }}
                                />
                            </div>
                        );
                    }
                    if (processedEntries.length > 0) {
                        return processedEntries.map((entry) => (
                            <Tooltip
                                key={entry.id}
                                text={`${entry.clientName}`}
                                placement="right"
                                enterDelay={500}
                                PopperProps={{
                                    modifiers: {
                                        flip: {
                                            behavior: ["right", "top"],
                                        },
                                    },
                                }}
                            >
                                <HistoryItem
                                    onClick={() =>
                                        onJobSelect?.(entry.projectId)
                                    }
                                    style={{
                                        cursor: onJobSelect
                                            ? "pointer"
                                            : "default",
                                    }}
                                >
                                    <HistoryItemLeft>
                                        <HistoryItemJob>
                                            {entry.job}
                                        </HistoryItemJob>
                                        <HistoryItemDate>
                                            {entry.date}
                                        </HistoryItemDate>
                                    </HistoryItemLeft>
                                    <HistoryItemHours>
                                        {entry.hours} h
                                        {entry.timeDayLabel &&
                                            ` - ${entry.timeDayLabel}`}
                                    </HistoryItemHours>
                                </HistoryItem>
                            </Tooltip>
                        ));
                    }
                    return <EmptyMessage>No recent entries</EmptyMessage>;
                })()}
            </HistoryItemsContainer>
        </HistorySection>
    );
}

HistorySectionComponent.propTypes = {
    recentEntriesData: PropTypes.object,
    assignmentsData: PropTypes.object,
    departmentAssignmentsData: PropTypes.object,
    onJobSelect: PropTypes.func,
};

export default HistorySectionComponent;
